import React, { useEffect, useState } from "react";

import { BrowserRouter, Route, Routes } from "react-router-dom";

import Home from "./Pages/Home";
import ProjectPage from "./Pages/ProjectPage";
import NotFound from "./Pages/NotFound";
//import BotWidget from "./Components/BotWidget";

import "./App.css";

/**
 * Fetches all data from individual JSON files under /data/ and
 * reassembles them into the same shape the rest of the app expects.
 *
 * File structure:
 *   /data/main.json          – personal / hero info
 *   /data/resume.json        – experience, education, skills
 *   /data/projects/_index.json        – ordered list of project file names
 *   /data/projects/<slug>.json        – one file per project
 */
const loadResumeData = async () => {
  // 1. Fetch the top-level files and the project index in parallel
  const [main, resume, projectIndex] = await Promise.all([
    fetch("/data/main.json").then((r) => {
      if (!r.ok) throw new Error("Failed to load main data");
      return r.json();
    }),
    fetch("/data/resume.json").then((r) => {
      if (!r.ok) throw new Error("Failed to load resume data");
      return r.json();
    }),
    fetch("/data/projects/_index.json").then((r) => {
      if (!r.ok) throw new Error("Failed to load project index");
      return r.json();
    }),
  ]);

  // 2. Fetch every project file listed in the index – in parallel
  const projects = await Promise.all(
    projectIndex.map((slug) =>
      fetch(`/data/projects/${slug}.json`).then((r) => {
        if (!r.ok) throw new Error(`Failed to load project: ${slug}`);
        return r.json();
      })
    )
  );

  // 3. Reassemble into the original shape
  return {
    main,
    resume,
    portfolio: { projects },
  };
};

const App = () => {
  const [resumeData, setResumeData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadData = () => {
    setLoading(true);
    setLoadError(false);
    loadResumeData()
      .then((data) => {
        setResumeData(data);
        setLoading(false);
      })
      .catch(() => {
        setLoadError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="app-loading" role="status" aria-live="polite">
        <div className="app-loading-spinner" aria-hidden="true" />
        <p className="app-loading-text">Loading…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="app-error" role="alert">
        <h1>Something went wrong</h1>
        <p>We couldn&apos;t load the site. Please check your connection and try again.</p>
        <button type="button" className="button" onClick={loadData}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {/* <BotWidget /> */}
      <Routes>
        <Route path="/" element={<Home resumeData={resumeData} />} />
        <Route
          path="/projects/:slug"
          element={<ProjectPage resumeData={resumeData} />}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
